import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Heart, Maximize4, InfoCircle, Message, Share } from "iconsax-react";

import MobileLayout from "../../../components/MobileLayout";
import type { DataTypeVerbete } from "../../../hooks/sinalario";
import { useAuth } from "../../../hooks/auth";
import { useGamification } from "../../../hooks/useGamification";
import { favoritesService } from "../../../services/favoritesService";

import {
  TagRow,
  LetterTag,
  HeartBtnBox,
  ImageCard,
  VideoCard,
  VideoPlayerFrame,
  MaximizeBtn,
  SpeedControlBox,
  SpeedTitle,
  SpeedButtonsRow,
  SpeedBtn,
  MeaningSection,
  MeaningHeader,
  InfoIconBox,
  MeaningTitleGroup,
  MeaningTitle,
  MeaningSubtitle,
  MeaningText,
  ActionButtonsGroup,
  ActionRowBtn,
} from "./styles";

type VerbeteLocationState = DataTypeVerbete & { autoPlayVideo?: boolean };

function VerbeteDetails() {
  const { state } = useLocation() as { state?: VerbeteLocationState };
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const { user } = useAuth();
  const { addPoints } = useGamification();

  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isFavorite, setIsFavorite] = useState(false);

  const viewedRef = useRef<string | null>(null);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  useEffect(() => {
    if (!state?.video || !state?.autoPlayVideo) return;
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.play().catch(() => undefined);
  }, [state?.video, state?.autoPlayVideo]);

  useEffect(() => {
    const key = state?.uid || state?.palavra;
    if (!key || viewedRef.current === key) return;
    viewedRef.current = key;
    addPoints("VIEW_WORD");
  }, [state?.uid, state?.palavra, addPoints]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user?.uid || !state) return;
      const favs = await favoritesService.getFavorites(user.uid);
      if (cancelled) return;
      const key = state.uid || state.palavra;
      setIsFavorite(favs.some((f) => (f.uid || f.palavra) === key));
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, state]);

  if (!state) {
    return (
      <MobileLayout headerTitle="Sinalário" headerType="private">
        <div style={{ padding: "20px", textAlign: "center" }}>
          <h2>Sinal não encontrado</h2>
          <button type="button" onClick={() => navigate(-1)}>
            Voltar
          </button>
        </div>
      </MobileLayout>
    );
  }

  const handleDactylology = () => {
    navigate("/dactylology", {
      state: { word: state.palavra, imageUri: state.image },
    });
  };

  const handleToggleFavorite = async () => {
    if (!user?.uid) return;
    const added = await favoritesService.toggleFavorite(user.uid, state);
    setIsFavorite(added);
    if (added) {
      await addPoints("FAVORITE");
    }
  };

  return (
    <MobileLayout headerTitle={state.palavra} headerType="private">
      <TagRow>
        <LetterTag>Letra: {state.palavra?.[0]?.toUpperCase()}</LetterTag>
        <HeartBtnBox $active={isFavorite} onClick={handleToggleFavorite}>
          <Heart size={24} variant={isFavorite ? "Bold" : "Linear"} />
        </HeartBtnBox>
      </TagRow>

      {/* Se não tiver vídeo, exibe a imagem */}
      {!state.video ? (
        <ImageCard>
          <img
            src={
              state.image ||
              "https://via.placeholder.com/400x300?text=Sem+Imagem"
            }
            alt={state.palavra}
          />
        </ImageCard>
      ) : (
        <VideoCard>
          <VideoPlayerFrame>
            <video
              ref={videoRef}
              src={state.video}
              autoPlay
              loop
              muted
              playsInline
              controls={false}
            />
            <MaximizeBtn onClick={() => videoRef.current?.requestFullscreen()}>
              <Maximize4 size={20} />
            </MaximizeBtn>
          </VideoPlayerFrame>

          <SpeedControlBox>
            <SpeedTitle>Velocidade</SpeedTitle>
            <SpeedButtonsRow>
              <SpeedBtn
                $active={playbackSpeed === 0.5}
                onClick={() => setPlaybackSpeed(0.5)}
              >
                Lento
              </SpeedBtn>
              <SpeedBtn
                $active={playbackSpeed === 0.75}
                onClick={() => setPlaybackSpeed(0.75)}
              >
                Devagar
              </SpeedBtn>
              <SpeedBtn
                $active={playbackSpeed === 1}
                onClick={() => setPlaybackSpeed(1)}
              >
                Normal
              </SpeedBtn>
              <SpeedBtn
                $active={playbackSpeed === 1.5}
                onClick={() => setPlaybackSpeed(1.5)}
              >
                Rápido
              </SpeedBtn>
            </SpeedButtonsRow>
          </SpeedControlBox>
        </VideoCard>
      )}

      <MeaningSection>
        <MeaningHeader>
          <InfoIconBox>
            <InfoCircle size={24} variant="Bold" />
          </InfoIconBox>
          <MeaningTitleGroup>
            <MeaningTitle>Significado</MeaningTitle>
            <MeaningSubtitle>Definição da palavra</MeaningSubtitle>
          </MeaningTitleGroup>
        </MeaningHeader>
        <MeaningText>
          {state.descricao ||
            "Sem motivo; sem razão; sem necessidade. (Adicione uma definição)"}
        </MeaningText>
      </MeaningSection>

      <ActionButtonsGroup>
        <ActionRowBtn onClick={handleDactylology}>
          <div className="icon-box">
            <Message size={24} variant="Bold" />
          </div>
          <div className="text-box">
            <h4>Ver Datilologia</h4>
            <span>Soletração em Libras</span>
          </div>
        </ActionRowBtn>

        <ActionRowBtn
          onClick={() => {
            if (navigator.share) {
              navigator.share({
                title: state.palavra,
                text: state.descricao,
                url: window.location.href,
              });
            }
          }}
        >
          <div className="icon-box">
            <Share size={24} variant="Bold" />
          </div>
          <div className="text-box">
            <h4>Compartilhar</h4>
            <span>Compartilhe este sinal</span>
          </div>
        </ActionRowBtn>
      </ActionButtonsGroup>
    </MobileLayout>
  );
}

export default VerbeteDetails;
